import {
  CustomCollectionsFieldConfig,
  Entry,
  OnboardingApplication,
} from "@dreambigger/shared/src/types";
import compileTemplate from "../../../utils/compileTemplate";
import { CrossEntryValidation } from "../types";
import shouldDisplayField from "./shouldDisplayField";

function fieldNoun(v: CrossEntryValidation): string {
  return v.fieldLabel || v.fieldKey;
}

/** Filter entries to those where the rule's parent field is currently
 *  displayed. When the rule carries no `fieldConfig` (legacy callers / tests),
 *  all entries are considered visible — same as pre-display-gating behavior. */
function visibleEntriesFor(
  rule: CrossEntryValidation,
  entries: Entry[],
  application: OnboardingApplication | undefined,
  allFieldConfigs: CustomCollectionsFieldConfig[] | undefined
): Entry[] {
  if (!rule.fieldConfig) return entries;
  return entries.filter((e) =>
    shouldDisplayField(
      rule.fieldConfig!,
      e,
      application,
      undefined,
      allFieldConfigs
    )
  );
}

/**
 * Validate all entries against cross-entry rules (global check).
 * Used by useStepLogic for step-level validation before advancing.
 *
 * Rules are skipped entirely when no entry has the parent field displayed
 * (e.g. `minCount: 1` on `isBeneficialOwner` is a no-op for a sole-prop
 * application where the BO checkbox is hidden by display conditions). Hidden
 * entries are also excluded from counts/totals — a user can't satisfy a
 * checkbox they can't see, so they shouldn't be expected to.
 */
export default function validateAllEntriesCrossRules({
  entries,
  validations,
  entryTitleTemplate,
  application,
  allFieldConfigs,
}: {
  entries: Entry[];
  validations: CrossEntryValidation[];
  entryTitleTemplate: string;
  application?: OnboardingApplication;
  allFieldConfigs?: CustomCollectionsFieldConfig[];
}): string | null {
  for (const v of validations) {
    const visible = visibleEntriesFor(v, entries, application, allFieldConfigs);
    if (visible.length === 0) continue;

    if (v.rule === "minCount") {
      const count = visible.filter((e) => e[v.fieldKey] === true).length;
      if (count < v.value) {
        return (
          v.message ||
          `At least ${v.value} ${fieldNoun(v)} ${
            v.value === 1 ? "is" : "are"
          } required`
        );
      }
    }
    if (v.rule === "maxCount") {
      const count = visible.filter((e) => e[v.fieldKey] === true).length;
      if (count > v.value) {
        return v.message || `Maximum ${v.value} ${fieldNoun(v)} allowed`;
      }
    }
    if (v.rule === "maxTotal") {
      const total = visible.reduce(
        (sum, e) => sum + (Number(e[v.fieldKey]) || 0),
        0
      );
      if (total > v.value) {
        return v.message || `${fieldNoun(v)} cannot exceed ${v.value}%`;
      }
    }
  }

  // Uniqueness checks (e.g. SSN)
  for (const v of validations) {
    if (v.rule !== "unique") continue;
    const visible = visibleEntriesFor(v, entries, application, allFieldConfigs);
    if (visible.length === 0) continue;
    const seen = new Map<string, string>();
    for (const e of visible) {
      const val = e[v.fieldKey];
      if (!val) continue;
      const strVal = String(val);
      const entryName = compileTemplate(entryTitleTemplate, e) || "an entry";
      if (seen.has(strVal)) {
        return (
          v.message ||
          `Duplicate ${fieldNoun(v)} found for ${entryName} and ${seen.get(
            strVal
          )}`
        );
      }
      seen.set(strVal, entryName);
    }
  }

  return null;
}
